import { useEffect, useState } from "react";
import { Plus, ArrowUp, ArrowDown, Pencil, Film } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PortfolioForm } from "./PortfolioForm";
import { ConfirmDelete } from "./RequestsList";

export const PortfolioManager = () => {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(undefined);

  useEffect(() => {
    api.get("/portfolio").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const save = async (form) => {
    try {
      if (editing) {
        const { data } = await api.put(`/admin/portfolio/${editing.id}`, form);
        setItems((x) => x.map((i) => (i.id === data.id ? data : i)));
      } else {
        const { data } = await api.post("/admin/portfolio", form);
        setItems((x) => [...x, data]);
      }
      toast.success("اتحفظ");
      setEditing(undefined);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/portfolio/${id}`); setItems((x) => x.filter((i) => i.id !== id)); toast.success("الفيديو اتمسح"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const move = async (idx, dir) => {
    const next = [...items];
    const j = idx + dir;
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
    try { await api.put("/admin/portfolio/reorder", { ids: next.map((i) => i.id) }); }
    catch (e) { toast.error(errMsg(e)); setItems(items); }
  };

  if (items === null) return <p className="text-purple-300/70">بنحمّل...</p>;

  return (
    <section data-testid="portfolio-manager">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-3xl text-white">فيديوهات البورتفوليو <span className="text-[var(--km-pink)]" data-testid="portfolio-count">({items.length})</span></h2>
        <button onClick={() => setEditing(null)} className="font-head inline-flex items-center gap-2 rounded-full bg-[var(--km-pink)] px-5 py-2.5 font-bold text-white" data-testid="portfolio-add-btn">
          <Plus className="h-4 w-4" /> إضافة فيديو
        </button>
      </div>
      {items.length === 0 ? (
        <div className="rounded-3xl border-2 border-dashed border-purple-500/30 p-12 text-center" data-testid="portfolio-admin-empty">
          <Film className="mx-auto mb-3 h-12 w-12 text-[var(--km-purple)]" />
          <p className="font-head text-lg text-white">البورتفوليو فاضي</p>
          <p className="text-sm text-purple-300/70">ضيف أول فيديو برابط YouTube أو Vimeo أو Embed.</p>
        </div>
      ) : (
        <ol className="space-y-3">
          {items.map((it, i) => (
            <li key={it.id} className="flex items-center gap-3 rounded-2xl border border-purple-500/25 bg-[var(--km-card)] p-3" data-testid={`portfolio-admin-item-${i}`}>
              <span className="font-display w-6 text-center text-xl text-[var(--km-purple)]">{i + 1}</span>
              <div className="grid h-16 w-28 shrink-0 place-items-center overflow-hidden rounded-xl bg-black">
                {it.thumbnail_url ? <img src={it.thumbnail_url} alt="" className="h-full w-full object-cover" /> : <Film className="h-6 w-6 text-purple-300/60" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-head truncate font-bold text-white" data-testid={`portfolio-admin-title-${i}`}>{it.title}</p>
                <p className="truncate text-xs text-purple-300/60" dir="ltr" style={{ textAlign: "right" }}>{it.video_url}</p>
              </div>
              <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
                <button disabled={i === 0} onClick={() => move(i, -1)} className="grid h-9 w-9 place-items-center rounded-full bg-white/5 text-white disabled:opacity-30" aria-label="لفوق" data-testid={`portfolio-move-up-${i}`}><ArrowUp className="h-4 w-4" /></button>
                <button disabled={i === items.length - 1} onClick={() => move(i, 1)} className="grid h-9 w-9 place-items-center rounded-full bg-white/5 text-white disabled:opacity-30" aria-label="لتحت" data-testid={`portfolio-move-down-${i}`}><ArrowDown className="h-4 w-4" /></button>
                <button onClick={() => setEditing(it)} className="grid h-9 w-9 place-items-center rounded-full bg-white/5 text-white hover:bg-[var(--km-purple)]" aria-label="تعديل" data-testid={`portfolio-edit-btn-${i}`}><Pencil className="h-4 w-4" /></button>
                <ConfirmDelete title={`تمسح «${it.title}»؟`} onConfirm={() => remove(it.id)} testId={`portfolio-delete-btn-${i}`} />
              </div>
            </li>
          ))}
        </ol>
      )}
      {editing !== undefined && (
        <PortfolioForm open key={editing?.id || "new"} initial={editing && { title: editing.title, description: editing.description, category: editing.category, video_url: editing.video_url }}
          onClose={() => setEditing(undefined)} onSave={save} />
      )}
    </section>
  );
};
